import { phoneFormatter } from "@/core/phone";

export type CustomerCardPersonalFormType = Omit<MyBrokerApi.Personal, "birthdate"> & {
    birthdate?: Date | null;
};

export type CustomerCardIdentityFormType = Omit<MyBrokerApi.Identity, "validFrom" | "validTo"> & {
    validFrom?: Date | null;
    validTo?: Date | null;
};

export type CustomerCardDriverLicenseFormType = Omit<MyBrokerApi.DriverLicense, "validUntil"> & {
    validUntil?: Date | null;
};

export type CustomerCardDetailsFormType = Pick<
    MyBrokerApi.CustomerCardRequest,
    "contact" | "officeAwareness" | "companyInfo"
> & {
    personal?: CustomerCardPersonalFormType;
    identity?: CustomerCardIdentityFormType;
    driverLicense?: CustomerCardDriverLicenseFormType;
};

export const customerCardDetailsDefaultValues = (
    policyholderDetails: MyBrokerApi.PolicyHolderPersonalDetails,
    address: MyBrokerApi.AddressChangeRequest,
): CustomerCardDetailsFormType => {
    const { isPhysicalPerson, currentDetails } = policyholderDetails;
    const { addressDetail } = address?.currentAddress || {};

    let defaultValues: CustomerCardDetailsFormType = {
        personal: {
            lastName: currentDetails?.lastName || "",
            street: addressDetail?.mainAddress || "",
            houseNumber: addressDetail?.number || "",
            boxNumber: addressDetail?.mailBox || "",
            zipCode: addressDetail?.postCode || "",
            city: addressDetail?.city || "",
            additionalInfo: addressDetail?.additionalAddress || "",
            country: {
                id: addressDetail?.country?.id || "",
            },
            nationality: {
                id: currentDetails?.nationality?.id || "",
            },
        },
        contact: {
            email: currentDetails?.contact?.email0 || "",
            phone1: currentDetails?.contact?.phone0 ? phoneFormatter(currentDetails?.contact?.phone0) : "",
            phone2: currentDetails?.contact?.phone1 ? phoneFormatter(currentDetails?.contact?.phone1) : "",
            phone3: currentDetails?.contact?.phone2 ? phoneFormatter(currentDetails?.contact?.phone2) : "",
            phoneType1: {
                id: currentDetails?.contact?.typePhone0?.id || "",
            },
            phoneType2: {
                id: currentDetails?.contact?.typePhone1?.id || "",
            },
            phoneType3: {
                id: currentDetails?.contact?.typePhone2?.id || "",
            },
        },
        officeAwareness: {
            comment: "",
        },
    };

    if (isPhysicalPerson) {
        defaultValues = {
            ...defaultValues,
            personal: {
                ...defaultValues.personal,
                title: {
                    id: currentDetails?.personalTitle?.id || "",
                },
                firstName: currentDetails?.firstName || "",
                birthdate: currentDetails?.birthdate || null,
                civilState: {
                    id: currentDetails?.maritalStatus?.id || "",
                },
            },
            identity: {
                idNumber: currentDetails?.identityCard?.number || "",
                validFrom: currentDetails?.identityCard?.validFrom || null,
                validTo: currentDetails?.identityCard?.validTo || null,
                issuedBy: currentDetails?.identityCard?.issuedBy || "",
                nationalRegistryNumber: currentDetails?.identityCard?.nationalNumber || "",
            },
            driverLicense: {
                hasDriverLicense: !!currentDetails?.driversLicense?.number,
                idNumber: currentDetails?.driversLicense?.number || "",
                validUntil: currentDetails?.driversLicense?.validUntil || null,
            },
        };
    } else {
        defaultValues = {
            ...defaultValues,
            personal: {
                ...defaultValues.personal,
                legalState: {
                    id: currentDetails?.legalStatus?.id,
                },
            },
            companyInfo: {
                bceNumber: currentDetails?.bankAccount?.idCrossedDbForBusinesses || "",
                establishmentNumber: currentDetails?.bankAccount?.establishmentNumber || "",
            },
        };
    }

    return defaultValues;
};
